import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { Loader } from "../../../Shared/Loader";
import { RemoveEmployeeDialog } from "../RemoveEmployee/RemoveEmployeeDialog";
import {
  Card, CardHead, StatusPill, StaffStatus, Stacked, Muted,
  Numeric, IconAction, GridWrap, Empty, dataGridSx,
} from "../staffStyles";
import { StaffStatusTabs } from "./StaffStatusTabs";
import { colors } from "../../../Shared/colors";
import { STAFF_STATUS, getStaffStatus } from "../../../Shared/staffStatus";

/**
 * Manage Staff, rebuilt.
 *
 * What changed and why:
 *
 * - The block control was an animated padlock that had to be decoded. Whether
 *   someone can log in is a fact about them, so it is now a labelled status
 *   column, with locking as an explicit action beside edit. Animation that
 *   decorates rather than explains was removed.
 * - A "removed" state exists now that employees can be retired properly, and it
 *   is visually distinct from "blocked" — they mean different things and
 *   previously looked identical.
 * - The three states are now tabs, defaulting to Active. Mixing current staff
 *   with leavers in one list made the common question — who works here — the
 *   one the screen answered least directly, and it let you act on a row without
 *   registering which kind of row it was.
 *
 * The four stat tiles that used to sit above the table are gone: the tab counts
 * say the same thing, in the control you use to act on it, without costing a
 * band of screen height on every visit.
 *
 * The removal flow deliberately lives behind its own dialog rather than a menu
 * item: it is irreversible, and the preview it opens with is the whole point.
 */

/** What an empty table means depends on which tab you are on. */
const EMPTY_STATES = {
  [STAFF_STATUS.ACTIVE]: {
    icon: GroupsOutlinedIcon,
    title: "No active staff",
    body: "Nobody here can currently log in. Add a staff member, or check the Blocked tab.",
  },
  [STAFF_STATUS.BLOCKED]: {
    icon: LockPersonOutlinedIcon,
    title: "Nobody is blocked",
    body: "Blocking suspends someone's login without removing them. Anyone you block will appear here until you unblock them.",
  },
  [STAFF_STATUS.REMOVED]: {
    icon: HistoryToggleOffOutlinedIcon,
    title: "Nobody has been removed",
    body: "People removed from the company stay here as a record, with their work already handed over.",
  },
};

/** The date part only — the hour someone was removed is never the question. */
const formatDay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const ManageStaffTable = ({
  staff = [],
  allStaff = [],
  counts,
  tab = STAFF_STATUS.ACTIVE,
  onTabChange = () => {},
  hasFilters = false,
  isFetching,
  onEdit,
  onToggleBlock,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const [removing, setRemoving] = useState(null);

  const rows = useMemo(
    () =>
      (staff || []).map((s) => ({
        ...s,
        id: s._id || s.id,
        status: getStaffStatus(s),
      })),
    [staff]
  );

  const isRemovedTab = tab === STAFF_STATUS.REMOVED;

  const columns = useMemo(() => {
    const base = [
      {
        field: "empName",
        headerName: "Employee",
        flex: 1,
        minWidth: 200,
        valueGetter: (params) =>
          `${params.row.firstName || ""} ${params.row.lastName || ""}`.trim(),
        renderCell: (params) => (
          <Stacked>
            <span className="primary">{params.value || "—"}</span>
            {params.row.email ? (
              <span className="sub">{params.row.email}</span>
            ) : null}
          </Stacked>
        ),
      },
      {
        field: "role",
        headerName: "Designation",
        width: 128,
        renderCell: (params) => (
          <Muted style={{ textTransform: "uppercase", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.5px" }}>
            {params.value || "—"}
          </Muted>
        ),
      },
      {
        field: "phoneNum",
        headerName: "Phone",
        width: 148,
        renderCell: (params) =>
          params.value ? <Numeric>{params.value}</Numeric> : <Muted>—</Muted>,
      },
      {
        field: "cnic",
        headerName: "CNIC",
        width: 152,
        renderCell: (params) =>
          params.value ? <Numeric>{params.value}</Numeric> : <Muted>—</Muted>,
      },
    ];

    // On the Removed tab, when they left and why is the reason you opened the
    // tab; the status pill would say "Removed" on every single row, which is
    // the one thing you already know.
    if (isRemovedTab) {
      base.push({
        field: "deactivatedAt",
        headerName: "Removed",
        width: 190,
        renderCell: (params) => (
          <Stacked>
            <span className="primary">{formatDay(params.value)}</span>
            {params.row.deactivationReason ? (
              <span className="sub">{params.row.deactivationReason}</span>
            ) : null}
          </Stacked>
        ),
      });
    } else {
      // Active and Blocked are both "still employed", so the pill still earns
      // its place: it is the difference between the two tabs at a glance when
      // you have arrived from a link rather than by clicking.
      base.unshift({
        field: "status",
        headerName: "Status",
        width: 112,
        renderCell: (params) => {
          const meta = StaffStatus[params.row.status];
          return (
            <StatusPill $color={meta.color} $bg={meta.bg}>
              {meta.label}
            </StatusPill>
          );
        },
      });
    }

    base.push({
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => {
        const name = params.row.firstName || "employee";

        // A removed record is history. Editing it, or restoring their login by
        // unblocking, would contradict the handover that already happened.
        if (params.row.status === STAFF_STATUS.REMOVED) {
          return <Muted style={{ fontSize: "0.75rem" }}>Read-only</Muted>;
        }

        return (
          <div style={{ display: "flex", gap: 2 }}>
            <Tooltip title="Edit details">
              <span>
                <IconAction
                  type="button"
                  aria-label={`Edit ${name}`}
                  $tone={colors.textBlue}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(params.row);
                  }}
                >
                  <EditOutlinedIcon />
                </IconAction>
              </span>
            </Tooltip>

            <Tooltip title={params.row.isBlock ? "Allow login" : "Block login"}>
              <span>
                <IconAction
                  type="button"
                  aria-label={
                    params.row.isBlock ? `Unblock ${name}` : `Block ${name}`
                  }
                  $tone={params.row.isBlock ? colors.moderate : colors.textMed}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleBlock(params.row);
                  }}
                >
                  {params.row.isBlock ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                </IconAction>
              </span>
            </Tooltip>

            <Tooltip title="Remove from the company and hand over their work">
              <span>
                <IconAction
                  type="button"
                  aria-label={`Remove ${name}`}
                  $tone={colors.veryHot}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRemoving(params.row);
                  }}
                >
                  <PersonRemoveOutlinedIcon />
                </IconAction>
              </span>
            </Tooltip>
          </div>
        );
      },
    });

    return base;
  }, [onEdit, onToggleBlock, isRemovedTab]);

  const emptyState = EMPTY_STATES[tab] || EMPTY_STATES[STAFF_STATUS.ACTIVE];
  const EmptyIcon = hasFilters ? SearchOffOutlinedIcon : emptyState.icon;

  return (
    <>
      <Card>
        <StaffStatusTabs value={tab} counts={counts} onChange={onTabChange} />

        <CardHead>
          {/*
            aria-live because the number changes as you type in the search box,
            with no other announcement that the table underneath moved.
          */}
          <span className="count" role="status" aria-live="polite">
            Showing <strong>{rows.length}</strong>{" "}
            {rows.length === 1 ? "employee" : "employees"}
            {hasFilters ? " matching your filters" : null}
          </span>
        </CardHead>

        <GridWrap>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isFetching}
            disableSelectionOnClick
            getRowClassName={(params) =>
              params.row.status === STAFF_STATUS.ACTIVE
                ? ""
                : `row-${params.row.status}`
            }
            page={page}
            onPageChange={onPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pagination
            rowsPerPageOptions={[25, 50, 100]}
            components={{
              NoRowsOverlay: () => (
                <Empty>
                  <EmptyIcon />
                  <h3>
                    {hasFilters ? "No matches on this tab" : emptyState.title}
                  </h3>
                  <p>
                    {hasFilters
                      ? "Nobody in this list matches your search. Clear the filters, or try another tab."
                      : emptyState.body}
                  </p>
                </Empty>
              ),
            }}
            sx={dataGridSx}
          />
          {isFetching ? <Loader /> : null}
        </GridWrap>
      </Card>

      <RemoveEmployeeDialog
        open={Boolean(removing)}
        employee={removing}
        staff={allStaff}
        onClose={() => setRemoving(null)}
      />
    </>
  );
};
